import { createPortal } from "react-dom";
import { type ReactNode, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../i18n/LanguageProvider";
import type { SupportedLang } from "../i18n/LanguageProvider";

type NavItem = {
  label?: string;
  i18nKey?: string;
  to: string;
  end?: boolean;
};

const MARKETING_LINKS: NavItem[] = [
  { i18nKey: "nav.home", to: "/", end: true },
  { i18nKey: "nav.pricing", to: "/pricing" },
  { i18nKey: "nav.products", to: "/products" },
  { i18nKey: "nav.services", to: "/services" },
  { i18nKey: "nav.faq", to: "/faq" },
  { i18nKey: "nav.support", to: "/support" }
];

type NavbarProps = {
  variant?: "marketing" | "dashboard";
  centerLinks?: NavItem[];
  showAuthButtons?: boolean;
  rightSlot?: ReactNode;
};

const KIRUSA = {
  red: "#D81F26",
  orange: "#F37321",
  text: "#414141",
  textMuted: "#6E6E6E",
  border: "#E5E5E5",
  bgWhite: "#FFFFFF",
  bgLight: "#F9F9F9",
  pill: "rgba(216,31,38,0.12)",
  pillHover: "rgba(216,31,38,0.06)"
};

export function Navbar({
  variant = "marketing",
  centerLinks,
  showAuthButtons = true,
  rightSlot
}: NavbarProps = {}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const baseLinks: NavItem[] =
    centerLinks ?? (variant === "marketing" ? MARKETING_LINKS : []);

  const links: NavItem[] = baseLinks.map((l) => ({
    ...l,
    label: l.i18nKey ? t(l.i18nKey) : l.label ?? ""
  }));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handle = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handle, { passive: true });
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);

  const MobileToggle = (
    <button
      type="button"
      aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
      aria-expanded={open}
      onClick={() => setOpen((v) => !v)}
      className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full transition"
      style={{
        border: `1px solid ${KIRUSA.border}`,
        color: KIRUSA.red,
        background: "transparent"
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.backgroundColor = KIRUSA.pillHover)
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.backgroundColor = "transparent")
      }
    >
      <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
    </button>
  );

  /* ---------------- DASHBOARD VARIANT ---------------- */
  if (variant === "dashboard") {
    return (
      <header className="sticky top-0 z-[60] bg-transparent px-3 pt-3 sm:px-4">
        <style>{cssTuning}</style>
        <nav
          className="nav-shell mx-auto flex w-full items-center justify-between gap-2 rounded-2xl bg-white/90 px-3 py-2 text-xs font-medium shadow-sm backdrop-blur flex-nowrap"
          style={{ color: KIRUSA.text }}
        >
          {/* Brand */}
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2 text-sm font-semibold whitespace-nowrap"
            style={{ color: KIRUSA.text }}
          >
            <img
              src="/static/ConvoBuddyLogo-40.webp"
              srcSet="
                /static/ConvoBuddyLogo-40.webp 1x,
                /static/ConvoBuddyLogo-60.webp 1.5x,
                /static/ConvoBuddyLogo-80.webp 2x
              "
              width={36}
              height={36}
              alt="ConvoBuddy logo"
              className="brand-logo"
            />
            <span
              className="brand-name hidden truncate text-[0.7rem] font-semibold uppercase tracking-[0.22em] sm:inline"
              style={{ color: KIRUSA.red }}
            >
              Convobuddy
            </span>
          </Link>

          {/* Center links */}
          {links.length > 0 && (
            <div className="hidden md:flex flex-1 min-w-0 items-center justify-center">
              <div className="nav-scroll mx-auto flex flex-nowrap items-center gap-2 overflow-x-auto">
                {links.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    className={() =>
                      "whitespace-nowrap rounded-full px-3 py-1.5 text-[0.78rem] transition"
                    }
                    style={({ isActive }) =>
                      isActive
                        ? {
                          background: KIRUSA.pill,
                          color: KIRUSA.red,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
                        }
                        : { color: "rgba(26,28,40,0.78)" }
                    }
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        KIRUSA.pillHover;
                      (e.currentTarget as HTMLElement).style.color = KIRUSA.red;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        "transparent";
                      (e.currentTarget as HTMLElement).style.color =
                        "rgba(26,28,40,0.78)";
                    }}
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Right + mobile */}
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 whitespace-nowrap">
              <LanguageSwitcher compact />
              {rightSlot}
              {showAuthButtons && !rightSlot && (
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[0.75rem] font-semibold transition"
                  style={{
                    border: `1px solid ${KIRUSA.red}`,
                    color: KIRUSA.red
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      KIRUSA.red;
                    (e.currentTarget as HTMLElement).style.color =
                      KIRUSA.bgWhite;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      "transparent";
                    (e.currentTarget as HTMLElement).style.color = KIRUSA.red;
                  }}
                >
                  <i className="fa-solid fa-right-to-bracket text-xs" />
                  <span>{t("nav.login")}</span>
                </Link>
              )}
            </div>
            {MobileToggle}
          </div>
        </nav>

        <MobileSheet
          open={open}
          onClose={() => setOpen(false)}
          links={links}
          rightSlot={rightSlot}
          showAuthButtons={showAuthButtons}
        />
      </header>
    );
  }

  /* ---------------- MARKETING NAVBAR (HOME, PRICING, …) ---------------- */
  const marketingHeaderClasses = cn(
    "sticky top-0 z-[60] px-3 py-3 sm:px-4",
    scrolled ? "drop-shadow-lg" : "drop-shadow-sm"
  );
  const marketingNavClasses = cn(
    "nav-shell mx-auto flex w-full items-center justify-between gap-3 rounded-[28px] px-3 py-2 text-sm font-medium backdrop-blur flex-nowrap",
    scrolled ? "bg-white/95 shadow-sm" : "bg-white/80 shadow-sm"
  );

  return (
    <header className={marketingHeaderClasses}>
      <style>{cssTuning}</style>
      <nav className={marketingNavClasses} style={{ color: "#1B1B1F" }}>
        {/* Brand */}
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 text-[1.02rem] font-semibold tracking-tight whitespace-nowrap"
          style={{ color: KIRUSA.text }}
        >
          <img
            src="/static/ConvoBuddyLogo-40.webp"
            srcSet="
              /static/ConvoBuddyLogo-40.webp 1x,
              /static/ConvoBuddyLogo-60.webp 1.5x,
              /static/ConvoBuddyLogo-80.webp 2x
            "
            width={40}
            height={40}
            alt="ConvoBuddy logo"
            className="brand-logo"
          />
          <span className="brand-name truncate">Convobuddy</span>
        </Link>

        {/* Center links */}
        {links.length > 0 && (
          <div className="hidden md:flex flex-1 min-w-0 items-center justify-center">
            <div className="nav-scroll mx-auto flex flex-nowrap items-center gap-2 overflow-x-auto">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={() =>
                    "whitespace-nowrap rounded-full px-4 py-2 text-sm transition"
                  }
                  style={({ isActive }) =>
                    isActive
                      ? {
                        background: KIRUSA.pill,
                        color: KIRUSA.red,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
                      }
                      : { color: "rgba(26,28,40,0.78)" }
                  }
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      KIRUSA.pillHover;
                    (e.currentTarget as HTMLElement).style.color = KIRUSA.red;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      "transparent";
                    (e.currentTarget as HTMLElement).style.color =
                      "rgba(26,28,40,0.78)";
                  }}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Right section — dropdown language + auth */}
        <div className="hidden md:flex items-center gap-3 text-sm font-semibold whitespace-nowrap shrink-0">
          {/* Desktop marketing: dropdown language selector */}
          <LanguageSwitcher asSelect />
          {rightSlot}
          {showAuthButtons && !rightSlot && (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition"
              style={{
                border: `1px solid ${KIRUSA.red}`,
                background: KIRUSA.red,
                color: "#FFFFFF"
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "#B6191F";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  KIRUSA.red;
              }}
            >
              <i className="fa-solid fa-right-to-bracket text-sm" />
              <span>{t("nav.login")}</span>
            </Link>
          )}
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 md:hidden">
          {showAuthButtons && !rightSlot && (
            <Link
              to="/login"
              className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold transition"
              style={{
                border: `1px solid ${KIRUSA.red}`,
                background: KIRUSA.red,
                color: "#FFFFFF"
              }}
            >
              {t("nav.login")}
            </Link>
          )}
          {MobileToggle}
        </div>
      </nav>

      <MobileSheet
        open={open}
        onClose={() => setOpen(false)}
        links={links}
        rightSlot={rightSlot}
        showAuthButtons={showAuthButtons}
      />
    </header>
  );
}

/* ------------ Language Switcher ------------ */
function LanguageSwitcher({
  compact,
  asSelect
}: {
  compact?: boolean;
  asSelect?: boolean;
}) {
  const { lang, setLang } = useLanguage();
  const { t } = useTranslation("common");

  const options: { code: SupportedLang; label: string }[] = [
    { code: "en", label: "EN" },
    { code: "hi", label: "हिन्दी" },
    { code: "fr", label: "FR" },
    { code: "es", label: "ES" }
  ];

  // Dropdown version (used on marketing desktop + mobile sheet)
  if (asSelect) {
    return (
      <div className="flex w-full flex-col gap-1 text-xs md:w-auto">
        <label className="text-[0.68rem] text-[#6E6E6E]">
          {t("nav.language")}
        </label>
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value as SupportedLang)}
          className="w-full rounded-full border bg-white px-3 py-1.5 text-xs md:w-auto"
          style={{ borderColor: KIRUSA.border, color: KIRUSA.text }}
        >
          {options.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Pill toggle version (dashboard, compact usage)
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1",
        compact ? "text-[0.7rem]" : "text-xs"
      )}
    >
      {!compact && (
        <span className="hidden sm:inline text-xs text-[#6E6E6E]">
          {t("nav.language")}
        </span>
      )}
      <div
        className="inline-flex items-center rounded-full border px-1.5 py-1 bg-white"
        style={{ borderColor: KIRUSA.border }}
      >
        {options.map((opt) => {
          const active = lang === opt.code;
          return (
            <button
              key={opt.code}
              type="button"
              onClick={() => setLang(opt.code)}
              className={cn(
                "px-2 py-0.5 rounded-full text-[0.7rem] font-semibold transition",
                active ? "shadow-sm" : "opacity-70"
              )}
              style={
                active
                  ? { background: KIRUSA.pill, color: KIRUSA.red }
                  : { background: "transparent", color: KIRUSA.textMuted }
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------ Mobile sheet ------------ */
function MobileSheet({
  open,
  onClose,
  links,
  rightSlot,
  showAuthButtons
}: {
  open: boolean;
  onClose: () => void;
  links: NavItem[];
  rightSlot?: ReactNode;
  showAuthButtons?: boolean;
}) {
  const { t } = useTranslation("common");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[80] md:hidden transition",
        open ? "pointer-events-auto" : "pointer-events-none"
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-menu-title"
    >
      <div
        className={cn(
          "absolute inset-0 bg-black/40 transition-opacity",
          open ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute left-1/2 top-[max(env(safe-area-inset-top,0px)+0.75rem,0.75rem)] w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/5 transition-all",
          "sm:w-[calc(100%-2rem)]",
          open ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0"
        )}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/convobuddy-mark.svg"
              width={36}
              height={36}
              alt="ConvoBuddy logo"
              className="h-9 w-9"
            />
            <span
              id="mobile-menu-title"
              className="text-sm font-semibold tracking-tight"
              style={{ color: KIRUSA.text }}
            >
              Convobuddy
            </span>
          </div>
          <button
            aria-label={t("nav.closeMenu")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full"
            style={{ background: KIRUSA.pill, color: KIRUSA.red }}
            onClick={onClose}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={onClose}
                className="rounded-2xl px-4 py-3 text-base font-semibold transition"
                style={({ isActive }: any) =>
                  isActive
                    ? { background: KIRUSA.pill, color: KIRUSA.red }
                    : { color: KIRUSA.text }
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="mt-4 grid gap-3">
            <LanguageSwitcher asSelect />

            {rightSlot ? (
              <div className="grid gap-2">{rightSlot}</div>
            ) : showAuthButtons ? (
              <NavLink
                to="/login"
                onClick={onClose}
                className="rounded-full border px-4 py-3 text-center text-base font-semibold"
                style={{
                  border: `1px solid ${KIRUSA.red}`,
                  background: KIRUSA.red,
                  color: "#FFFFFF"
                }}
              >
                {t("nav.login")}
              </NavLink>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------ Inline CSS (same as before, plus scroll hiding) ------------ */
const cssTuning = `
@media (max-width: 374px) {
  .nav-shell { padding: 6px 8px !important; border-radius: 14px !important; }
  .brand-logo { width: 28px !important; height: 28px !important; }
  .brand-name { font-size: .9rem !important; }
}
@media (min-width: 375px) and (max-width: 425px) {
  .nav-shell { padding: 8px 10px !important; border-radius: 16px !important; }
}
@media (min-width: 426px) and (max-width: 600px) {
  .nav-shell { padding: 10px 12px !important; border-radius: 18px !important; }
}
@media (min-width: 600px) and (max-width: 768px) {
  .nav-shell { padding: 10px 14px !important; }
}
@media (min-width: 769px) and (max-width: 1024px) {
  .nav-shell { padding: 10px 16px !important; }
}
@media (min-width: 1025px) and (max-width: 1280px) {
  .nav-shell { padding: 10px 18px !important; }
}
@media (min-width: 1281px) and (max-width: 1440px) {
  .nav-shell { padding: 12px 20px !important; }
}
@media (min-width: 1441px) and (max-width: 1920px) {
  .nav-shell { padding: 12px 22px !important; }
}
@media (min-width: 1921px) {
  .nav-shell { padding: 14px 24px !important; }
}

/* hide tiny horizontal scrollbar for center nav on overflow */
.nav-scroll::-webkit-scrollbar { display: none; }
.nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
`;
